import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { AcceptanceResult } from '@arcanum-hq/shared-types';

// Re-export so the AcceptUnderwritingButton can import a stable name from
// the same module that defines the mutation hook. Phase 4 Session 4 gap-
// analysis (2026-05-03) consolidated the previously-duplicated local
// `AcceptUnderwritingResult` interface onto shared-types' `AcceptanceResult`
// — the underlying `{eventId, versionLabel, summary}` shape predates the
// agent-tool-to-API-endpoint relocation and now serves the API endpoint's
// response contract instead.
export type AcceptUnderwritingResult = AcceptanceResult;

interface AcceptUnderwritingBody {
  confidenceFlag: 'high' | 'medium' | 'low';
  notes?: string;
}

interface ApiAcceptUnderwritingResponse {
  success: boolean;
  data: AcceptanceResult;
}

/**
 * Accept the current model state as an immutable underwriting record.
 *
 * Replaces the deleted `accept_underwriting` agent tool (prompt-langgraph-
 * optimization Phase 4 Session 2, 2026-05-03). The agent can RECOMMEND
 * acceptance in chat, but only the user-confirmed click that drives this
 * mutation writes the event. Mounted inside `<VariablesPanel>` header.
 *
 * On success: invalidates both the per-conversation event list and the
 * user-wide list (events list query key is preserved for future consumers).
 * `retry: 0` because acceptance is idempotency-sensitive — the user
 * resubmits via the modal instead of silent retries.
 */
export function useAcceptUnderwriting(conversationId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: AcceptUnderwritingBody): Promise<AcceptanceResult> => {
      if (!conversationId) {
        throw new Error('Cannot accept underwriting: no active conversation');
      }
      const res = await api.post<ApiAcceptUnderwritingResponse>(
        `/api/conversations/${conversationId}/underwriting-events`,
        body,
      );
      return res.data;
    },
    retry: 0,
    onSuccess: () => {
      if (!conversationId) return;
      queryClient.invalidateQueries({
        queryKey: queryKeys.underwritingEvents.byConversation(conversationId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.underwritingEvents.list(),
      });
    },
  });
}
